<script setup lang="ts">
import { useVModel } from '@vueuse/core';
import { onBeforeUnmount, onMounted, PropType, reactive, ref, watch } from 'vue';
// @ts-expect-error (components lack types)
import { AisClearRefinements, AisStats } from 'vue-instantsearch/vue3/es';
import { useRoute } from 'vue-router';

import ToggleButton from '@/components/base/ToggleButton.vue';
import BaseBodyText from '@/components/base/typography/BaseBodyText.vue';
import ClearRefinements from '@/components/search/widgets/ClearRefinements.vue';
import CollapsibleRefinementList from '@/components/search/widgets/CollapsibleRefinementList.vue';
import CollapsibleSortBy from '@/components/search/widgets/CollapsibleSortBy.vue';
import SearchBox from '@/components/search/widgets/SearchBox.vue';
import overlayScrollLock from '@/utils/overlayScrollLock';
import { scrollToTarget } from '@/utils/scrollTo';
import { RefinementMenu } from '@/utils/searchRefinementMenus';
import { waitABit } from '@/utils/waitABit';

const props = defineProps({
  refinementMenus: { required: true, type: Array as PropType<RefinementMenu[]> },
  showBusinessPlp: { required: false, type: Boolean, default: false },
  sortByItems: { required: true, type: Array as PropType<{ label: string; value: string }[]> },
  // optional props
  menuLimit: { required: false, type: Number, default: 25 },
  panelGroupLocator: { required: false, type: String, default: 'panel-group' },
  whitelistedRefinements: {
    required: false,
    type: Array as PropType<string[]>,
    default: () => [],
  },
  wholesaleOnly: { required: false, type: Boolean },
});

const route = useRoute();

const revealMobileMenu = ref(false);
const optionCountByFacet = reactive<Record<string, number>>({});
const showFilters = reactive<Record<'sortBy' | string, boolean>>({
  sortBy: false,
});
props.refinementMenus.forEach((menu) => {
  optionCountByFacet[menu.attribute] = 0;
  showFilters[menu.attribute] = false;
});

const emit = defineEmits(['update:wholesaleOnly']);
const wholesaleOnly = useVModel(props, 'wholesaleOnly', emit);

let killInitMenus = false;
const initMenus = () => {
  if (killInitMenus) return undefined;
  props.refinementMenus.forEach((menu) => {
    showFilters[menu.attribute] = false;
  });
  if (window.innerWidth >= 891) {
    if (Object.values(optionCountByFacet).every((count) => count === 0)) {
      return waitABit({ timeout: 250, window }).then(() => {
        initMenus();
      });
    }
    const expansionLimit = 3;
    let expanded = 0;
    props.refinementMenus.forEach((menuWidget) => {
      if (expanded === expansionLimit) return;
      if (!optionCountByFacet[menuWidget.attribute]) return;
      showFilters[menuWidget.attribute] = true;
      expanded += 1;
    });
  }
  return undefined;
};

const closeOverlay = () => {
  overlayScrollLock.unlockOnClose();
  revealMobileMenu.value = false;
};
const openOverlay = () => {
  revealMobileMenu.value = true;
  overlayScrollLock.lockOnOpen();
};
defineExpose({ openOverlay });
const scrollToTopOfResults = () => {
  scrollToTarget('.ais-Hits');
};

let stopWatchingMenu: () => void;
onMounted(() => {
  overlayScrollLock.bindScrollListener();
  initMenus();

  stopWatchingMenu = watch(
    () => route.path,
    () => {
      props.refinementMenus.forEach((menu) => {
        optionCountByFacet[menu.attribute] = 0;
      });
      initMenus();
    },
  );
});
onBeforeUnmount(() => {
  overlayScrollLock.unbindScrollListener();
  killInitMenus = true;
  stopWatchingMenu();
});
</script>

<template>
  <div
    class="search-control-panel"
    :class="{ shown: revealMobileMenu }"
    data-test="search-control-panel"
  >
    <div class="panel-header hidden-md hidden-lg hidden-xl">
      <strong>Filter &amp; Sort</strong>
      <a href="#" class="close-overlay" @click.prevent="closeOverlay()" data-test="close-overlay"
        >✕</a
      >
    </div>
    <div class="hidden-xs hidden-sm">
      <slot name="search-bar">
        <SearchBox data-test="searchBox" />
      </slot>
      <div class="panel-heading with-border" aria-label="Filter By">
        <strong>FILTER BY</strong>
        <AisClearRefinements class="d-inline-block" :excluded-attributes="whitelistedRefinements">
          <template v-slot:default="{ canRefine, refine }">
            <div>
              <a
                v-show="canRefine"
                href="#"
                class="clear-filters"
                data-test="clear-filters"
                @click.prevent="refine"
                >Clear Filters</a
              >
            </div>
          </template>
        </AisClearRefinements>
      </div>
    </div>
    <div class="panel-group" :data-test="panelGroupLocator">
      <CollapsibleSortBy
        v-model="showFilters.sortBy"
        header="SORT BY"
        :items="sortByItems"
        class="hidden-md hidden-lg hidden-xl"
        data-test="mobile-sort-by"
      />
      <div v-if="showBusinessPlp" class="py-2 border-b border-solid border-neutral-200">
        <ToggleButton v-model="wholesaleOnly" id="select-wholesale">
          <BaseBodyText>Available for Wholesale</BaseBodyText>
        </ToggleButton>
      </div>
      <CollapsibleRefinementList
        v-for="menu in refinementMenus"
        v-model="showFilters[menu.attribute]"
        v-model:optionCount="optionCountByFacet[menu.attribute]"
        :key="menu.attribute"
        :header="menu.header"
        :attribute="menu.attribute"
        :operator="menu.operator"
        :limit="menuLimit"
      />
    </div>
    <div class="panel-footer hidden-md hidden-lg hidden-xl">
      <ClearRefinements
        class="d-inline-block"
        :excluded-attributes="whitelistedRefinements"
        @click="closeOverlay"
      />
      <AisStats>
        <template v-slot:default="{ nbHits }">
          <button
            class="view-results"
            data-test="view-results"
            @click.prevent="
              closeOverlay();
              scrollToTopOfResults();
            "
          >
            See {{ nbHits }} Results
          </button>
        </template>
      </AisStats>
    </div>
  </div>
</template>
